import { useCallback, useRef, useState } from "react";
import {
  accessioningService,
  type AccessioningData,
  type SpecimenIdentifierPreview,
} from "../services/workflows.service";
import { getExpectedSpecimenIdentifiers } from "../utils/grossingSpecimenIdentifiers";
import { getApiErrorStatus } from "../utils/apiError";

interface SaveOptions {
  caseId: string | null;
  persistedSpecimens: Array<{ id: string; number: string }>;
  prepare: () => Promise<void>;
  getData: () => AccessioningData;
  save: (data: AccessioningData) => Promise<unknown>;
  onSaved: (reprintRequired: boolean) => void;
}
const SAVE_ERROR =
  "The specimen identifiers could not be saved. Check that the case is still in Grossing, the identifiers are unique, and no specimen has been signed or billed.";

export function useGrossingSpecimenIdentifierSave(options: SaveOptions) {
  const [preview, setPreview] = useState<SpecimenIdentifierPreview>();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const pending = useRef<AccessioningData>();
  const busy = useRef(false);

  const savePending = useCallback(
    async (confirmLabelReprint: boolean) => {
      const data = pending.current;
      if (!data?.specimenIdentifierEdits) return;
      await options.save({
        ...data,
        specimenIdentifierEdits: {
          ...data.specimenIdentifierEdits,
          mode: "save",
          confirmLabelReprint,
        },
      });
      pending.current = undefined;
      setPreview(undefined);
      options.onSaved(confirmLabelReprint);
    },
    [options]
  );

  const recordFailure = useCallback((caught: unknown) => {
    setError(
      getApiErrorStatus(caught) === 400
        ? "Check the specimen identifiers. Each must be nonempty, contain no semicolon, and be unique within this case."
        : SAVE_ERROR
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!options.caseId || busy.current) return;
    busy.current = true;
    setIsSaving(true);
    setError(undefined);
    try {
      await options.prepare();
      const data = options.getData();
      const expectedIdentifiers = getExpectedSpecimenIdentifiers(
        options.persistedSpecimens
      );
      const result = await accessioningService.previewSpecimenIdentifiers(
        options.caseId,
        data.specimens,
        expectedIdentifiers
      );
      pending.current = {
        ...data,
        specimenIdentifierEdits: { mode: "save", expectedIdentifiers },
      };
      if (result.printedMaterialTypes.length) setPreview(result);
      else await savePending(false);
    } catch (caught) {
      recordFailure(caught);
    } finally {
      busy.current = false;
      setIsSaving(false);
    }
  }, [options, recordFailure, savePending]);

  const handleConfirm = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setIsSaving(true);
    setError(undefined);
    try {
      await savePending(true);
    } catch (caught) {
      recordFailure(caught);
    } finally {
      busy.current = false;
      setIsSaving(false);
    }
  }, [recordFailure, savePending]);

  const handleCancel = useCallback(() => {
    if (busy.current) return;
    pending.current = undefined;
    setPreview(undefined);
    setError(undefined);
  }, []);

  return { preview, isSaving, error, handleSave, handleConfirm, handleCancel };
}
